import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { naira } from "../../lib/format";
import { HC } from "./theme";
import { Card, SectionHead, Pill, Btn, Table, Field, Input, Select } from "./ui";
import { toast } from "sonner";
import { Download, Search } from "lucide-react";
import { downloadReport } from "../accounting/exportHelpers";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";

export default function Payslips() {
  const [rows, setRows] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [period, setPeriod] = useState("");
  const [q, setQ] = useState("");
  const [slip, setSlip] = useState(null);

  useEffect(() => {
    api.get("/hr/payroll/runs").then((r) => {
      const ps = [...new Set(r.map((x) => x.period))];
      setPeriods(ps);
      setPeriod(ps[0] || "");
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => {
    if (!period) return;
    api.get("/hr/payslips", { params: { period } }).then(setRows).catch((e) => toast.error(formatApiError(e)));
  }, [period]);

  const filtered = rows.filter((r) => !q || (r.employee_name || "").toLowerCase().includes(q.toLowerCase()) || (r.employee_no || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="hr-payslips-page">
      <Card>
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          <Field label="Payroll period" className="lg:w-56">
            <Select data-testid="slip-period" value={period} onChange={(e) => setPeriod(e.target.value)}>
              {periods.map((p) => <option key={p} value={p}>{p}</option>)}
            </Select>
          </Field>
          <Field label="Search" className="flex-1">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: HC.soft }} />
              <Input data-testid="slip-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Employee name or number" className="pl-9" />
            </div>
          </Field>
        </div>
      </Card>

      <Card>
        <SectionHead title={`Payslips — ${period || "—"} (${filtered.length})`} sub="Click a row to preview; download a branded PDF payslip" />
        <Table
          testid="hr-payslips-table" rows={filtered} empty="No payslips for this period" onRowClick={setSlip}
          columns={[
            { key: "employee_name", label: "EMPLOYEE", render: (r) => (
                <span><span className="block font-semibold">{r.employee_name}</span>
                  <span className="block text-[11px]" style={{ color: HC.soft }}>{r.employee_no}</span></span>) },
            { key: "department_name", label: "DEPARTMENT" },
            { key: "location_name", label: "LOCATION" },
            { key: "gross", label: "GROSS", align: "right", render: (r) => <span className="tabular-nums">{naira(r.gross)}</span> },
            { key: "total_deductions", label: "DEDUCTIONS", align: "right", render: (r) => <span className="tabular-nums">{naira(r.total_deductions)}</span> },
            { key: "net", label: "NET PAY", align: "right", render: (r) => <span className="tabular-nums font-semibold">{naira(r.net)}</span> },
            { key: "payment_status", label: "PAYMENT", render: (r) => <Pill status={r.payment_status} /> },
            { key: "pdf", label: "", align: "right", render: (r) => (
                <Btn variant="outline" className="h-8 !px-2.5" data-testid={`payslip-pdf-${r.id}`}
                  onClick={(ev) => { ev.stopPropagation(); downloadReport(`/hr/payslips/${r.id}/pdf`, `payslip-${r.employee_no}-${r.period}.pdf`); }}>
                  <Download size={13} />
                </Btn>) },
          ]}
        />
      </Card>

      <Dialog open={!!slip} onOpenChange={(o) => !o && setSlip(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="hr-payslip-preview">
          {slip && (
            <>
              <DialogHeader>
                <DialogTitle>{slip.employee_name} — {slip.period}</DialogTitle>
                <DialogDescription>{slip.employee_no} · {slip.department_name} · {slip.location_name}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] font-bold tracking-[0.14em] mb-2" style={{ color: HC.soft }}>EARNINGS</div>
                  {(slip.earnings || []).map((e, i) => (
                    <div key={i} className="flex items-center justify-between text-[12.5px] py-1 border-b" style={{ borderColor: HC.borderSoft }}>
                      <span style={{ color: HC.muted }}>{e.name}</span>
                      <span className="tabular-nums" style={{ color: HC.text }}>{naira(e.amount)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between text-[13px] font-semibold pt-2">
                    <span>Gross</span><span className="tabular-nums">{naira(slip.gross)}</span>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-bold tracking-[0.14em] mb-2" style={{ color: HC.soft }}>DEDUCTIONS</div>
                  {(slip.deductions || []).map((e, i) => (
                    <div key={i} className="flex items-center justify-between text-[12.5px] py-1 border-b" style={{ borderColor: HC.borderSoft }}>
                      <span style={{ color: HC.muted }}>{e.name}</span>
                      <span className="tabular-nums" style={{ color: HC.red }}>{naira(e.amount)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between text-[13px] font-semibold pt-2">
                    <span>Total deductions</span><span className="tabular-nums">{naira(slip.total_deductions)}</span>
                  </div>
                </div>
              </div>
              <div className="rounded-xl p-4 flex items-center justify-between" style={{ background: HC.burgundy }}>
                <span className="text-white font-brand font-bold">NET PAY</span>
                <span className="text-white font-brand font-bold text-xl tabular-nums">{naira(slip.net)}</span>
              </div>
              {!!slip.employer_contributions?.length && (
                <div className="text-[12px]" style={{ color: HC.muted }}>
                  Employer contributions: {slip.employer_contributions.map((c) => `${c.name} ${naira(c.amount)}`).join(", ")}
                </div>
              )}
              <Btn onClick={() => downloadReport(`/hr/payslips/${slip.id}/pdf`, `payslip-${slip.employee_no}-${slip.period}.pdf`)}
                data-testid="payslip-download"><Download size={15} /> Download PDF</Btn>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
